import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Email,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { EmailConfirmationPending } from './forms/EmailConfirmationPending';

interface InvitationSignUpProps {
  email: string;
  token: string;
  onSuccess: () => void;
}

const InvitationSignUp: React.FC<InvitationSignUpProps> = ({ email, token, onSuccess }) => {
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmailConfirmationPending, setIsEmailConfirmationPending] = useState(false);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!formData.displayName.trim()) {
      return '表示名を入力してください';
    }
    if (formData.displayName.length < 2) {
      return '表示名は2文字以上で入力してください';
    }
    if (!formData.password) {
      return 'パスワードを入力してください';
    }
    if (formData.password.length < 6) {
      return 'パスワードは6文字以上で入力してください';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'パスワードが一致しません';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // メタデータに招待トークンと表示名を含める
      const result = await signUp(email, formData.password, {
        display_name: formData.displayName,
        invitation_token: token,
      });
      
      // セッションがない場合はメール確認が必要
      if (!result.session) {
        setIsEmailConfirmationPending(true);
        setLoading(false);
        return;
      }
      
      // セッションがある場合は即座に完了
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'アカウント作成に失敗しました');
      setLoading(false);
    }
  };

  // メール確認待ち画面を表示
  if (isEmailConfirmationPending) {
    return (
      <EmailConfirmationPending
        email={email}
        onBackToLogin={() => {
          // 招待画面に戻る場合は状態をリセット
          setIsEmailConfirmationPending(false);
          setFormData({
            displayName: '',
            password: '',
            confirmPassword: '',
          });
          setError(null);
        }}
      />
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        新規アカウント作成
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        招待を受諾するには、まずアカウントを作成してください
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="メールアドレス"
          value={email}
          disabled
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="表示名"
          value={formData.displayName}
          onChange={handleChange('displayName')}
          placeholder="例: 山田太郎"
          helperText="システム内で表示される名前です"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="パスワード"
          value={formData.password}
          onChange={handleChange('password')}
          helperText="6文字以上で入力してください"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showConfirmPassword ? 'text' : 'password'}
          label="パスワード（確認）"
          value={formData.confirmPassword}
          onChange={handleChange('confirmPassword')}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading}
          sx={{ mb: 2 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'アカウントを作成して招待を受諾'
          )}
        </Button>
      </form>
    </Paper>
  );
};

export default InvitationSignUp;